import { Info, RefreshCw } from 'lucide-react'
import type { ReactNode } from 'react'

import type { GroupHealth, GroupHealthConfig } from '@/api/types'
import { ProblemAlert } from '@/components/error-surface'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { formatDateTime, formatShortDate } from '@/lib/datetime'
import { cn } from '@/lib/utils'

import { formatRatio, formatScore } from './constants'
import { HealthIndicator, RoundEligibility } from './health'
import { describeHealth } from './health-presentation'
import { AttestHealthAction } from './attest-health'
import { useRecomputeGroupHealth } from './mutations'
import { useGroupPermissions } from './permissions'

/**
 * The health assessment, with its inputs on the screen.
 *
 * This panel exists because of one conversation: a group is refused another round, and somebody
 * asks why. The answer has to be here — the three inputs, the weight each carried, the window they
 * were measured over, and the threshold the total was compared against — rather than in a
 * calculation nobody in the branch can see.
 *
 * The weights shown are the ones echoed back on the snapshot, not the ones configured today. A
 * score read months later has to be readable against the configuration that produced it, which is
 * why `GroupHealthSnapshot` rows are append-only and carry their own weights.
 */
export function GroupHealthPanel({ groupId, health }: { groupId: string; health: GroupHealth }) {
  const permissions = useGroupPermissions()
  const presentation = describeHealth({ health })
  const recompute = useRecomputeGroupHealth(groupId)

  const config = health.config
  const anyInputMissing =
    health.onTimeRepaymentRate === null ||
    health.par30Ratio === null ||
    health.attendanceRate === null

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex-wrap gap-3">
          <div className="min-w-0 space-y-1">
            <CardTitle>Health assessment</CardTitle>
            <p className="text-xs text-muted">
              {health.computedAtUtc
                ? `Computed ${formatDateTime(health.computedAtUtc)}.`
                : 'Never computed. Nothing has been stored for this group yet.'}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
          <AttestHealthAction groupId={groupId} />
          {permissions.canViewHealth ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => recompute.mutate()}
              disabled={recompute.isPending}
            >
              <RefreshCw className={cn(recompute.isPending && 'animate-spin')} />
              {recompute.isPending ? 'Recomputing…' : 'Recompute now'}
            </Button>
          ) : null}
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex min-w-0 flex-col gap-1">
              <span className="text-2xs font-semibold tracking-wide text-muted uppercase">
                Score, out of 100
              </span>
              <div className="flex items-center gap-2">
                {presentation.isScored ? (
                  <span className="numeric text-2xl font-semibold text-heading tabular-nums">
                    {formatScore(health.score)}
                  </span>
                ) : null}
                <HealthIndicator health={presentation} />
              </div>
            </div>

            <div className="flex min-w-0 flex-col gap-1">
              <span className="text-2xs font-semibold tracking-wide text-muted uppercase">
                Another round
              </span>
              <RoundEligibility health={presentation} />
            </div>
          </div>

          <Alert tone={presentation.tone === 'success' ? 'success' : presentation.tone === 'danger' ? 'danger' : 'info'}>
            <Info aria-hidden />
            <div className="space-y-1">
              <AlertTitle>{presentation.outcomeLabel}</AlertTitle>
              <AlertDescription>{presentation.explanation}</AlertDescription>
            </div>
          </Alert>

          {recompute.isError ? <ProblemAlert error={recompute.error} /> : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="min-w-0">
            <CardTitle>The three inputs</CardTitle>
            <p className="mt-0.5 text-xs text-muted">
              Measured over {formatShortDate(health.windowFrom)} to {formatShortDate(health.windowTo)} —{' '}
              {config.windowWeeks} weeks, of which at least {config.minimumWindowWeeks} must be real
              history before a score is produced.
            </p>
          </div>
        </CardHeader>

        <CardContent className="space-y-3">
          <InputRow
            name="On-time repayment"
            formula="On-time instalments ÷ instalments due in the window"
            value={formatRatio(health.onTimeRepaymentRate)}
            available={health.onTimeRepaymentRate !== null}
            unavailableNote="The loan book has not shipped, so nothing in this system knows what was repaid on time. It is unavailable, not zero."
            weight={config.onTimeRepaymentWeight}
            contribution={
              health.onTimeRepaymentRate === null
                ? null
                : config.onTimeRepaymentWeight * health.onTimeRepaymentRate * 100
            }
          />

          <InputRow
            name="Portfolio at risk over 30 days"
            formula={`1 − min(PAR30 ÷ ${formatRatio(config.parCap)} cap, 1) — a lower ratio scores higher`}
            value={formatRatio(health.par30Ratio)}
            available={health.par30Ratio !== null}
            unavailableNote="Arrears on member loans cannot be answered until the loan book exists. It is unavailable, not zero."
            weight={config.portfolioAtRiskWeight}
            contribution={
              health.par30Ratio === null
                ? null
                : config.portfolioAtRiskWeight *
                  (1 - Math.min(health.par30Ratio / config.parCap, 1)) *
                  100
            }
          />

          <InputRow
            name="Attendance"
            formula="Attended member-meetings ÷ expected member-meetings, excused absences excluded from both sides"
            value={formatRatio(health.attendanceRate)}
            available={health.attendanceRate !== null}
            unavailableNote="No meetings have been held in this window, so there is no ratio to compute."
            weight={config.attendanceWeight}
            contribution={
              health.attendanceRate === null
                ? null
                : config.attendanceWeight * health.attendanceRate * 100
            }
          />

          {anyInputMissing ? (
            <Alert tone="warning">
              <Info aria-hidden />
              <div className="space-y-1">
                <AlertTitle>An unavailable input is not a zero</AlertTitle>
                <AlertDescription>
                  A group with no repayment history and a group that repaid everything on time both
                  have no missed instalments. Read as 1.0 the first one qualifies for credit on its
                  formation day; read as 0.0 the second is punished for having done nothing wrong.
                  Neither is true, so no score is produced at all — and a group with no score has
                  not qualified.
                </AlertDescription>
              </div>
            </Alert>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="min-w-0">
            <CardTitle>The configuration this result was produced under</CardTitle>
            <p className="mt-0.5 text-xs text-muted">
              Echoed back with the assessment rather than looked up now, so a score acted on months
              ago still reads against the weights that were in force when it was taken.
            </p>
          </div>
        </CardHeader>
        <CardContent>
          <ConfigGrid config={config} />
        </CardContent>
      </Card>
    </div>
  )
}

/**
 * One input, with what it is, what it was, what it was worth, and what it contributed.
 *
 * A missing figure is rendered as words rather than as a dash on its own. "—" in a column of
 * percentages reads as zero to anyone moving quickly, and this is the column somebody moving
 * quickly reads before refusing a loan round.
 */
function InputRow({
  name,
  formula,
  value,
  available,
  unavailableNote,
  weight,
  contribution,
}: {
  name: string
  formula: string
  value: string
  available: boolean
  unavailableNote: string
  weight: number
  contribution: number | null
}) {
  const maximum = weight * 100

  return (
    <div
      className={cn(
        'grid gap-3 rounded-card border p-3 sm:grid-cols-[minmax(0,1fr)_auto]',
        available ? 'border-border-subtle bg-surface' : 'border-dashed border-warning-border bg-warning-subtle',
      )}
    >
      <div className="min-w-0 space-y-1">
        <p className="text-sm font-medium text-heading">{name}</p>
        <p className="text-xs text-muted">{formula}</p>
        {available ? null : <p className="text-xs text-warning-text">{unavailableNote}</p>}
      </div>

      <dl className="grid grid-cols-3 gap-x-4 gap-y-1 sm:text-right">
        <Figure label="Measured" value={available ? value : 'Unavailable'} emphasise={available} />
        <Figure label="Weight" value={weight.toFixed(2)} />
        <Figure
          label={`of ${maximum.toFixed(1)}`}
          value={contribution === null ? '—' : contribution.toFixed(1)}
          emphasise={contribution !== null}
        />
      </dl>
    </div>
  )
}

function Figure({
  label,
  value,
  emphasise = false,
}: {
  label: string
  value: ReactNode
  emphasise?: boolean
}) {
  return (
    <div className="min-w-0">
      <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">{label}</dt>
      <dd
        className={cn(
          'numeric mt-0.5 text-sm tabular-nums',
          emphasise ? 'font-semibold text-heading' : 'text-body',
        )}
      >
        {value}
      </dd>
    </div>
  )
}

function ConfigGrid({ config }: { config: GroupHealthConfig }) {
  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
      <Figure label="Qualifying threshold" value={`${formatScore(config.qualifyThreshold)} / 100`} />
      <Figure label="Watch threshold" value={`${formatScore(config.watchThreshold)} / 100`} />
      <Figure label="PAR cap" value={formatRatio(config.parCap)} />
      <Figure label="Window" value={`${String(config.windowWeeks)} weeks`} />
      <Figure label="Minimum window" value={`${String(config.minimumWindowWeeks)} weeks`} />
      <Figure label="Defaulters allowed" value={String(config.maxDefaultersAllowed)} />
    </dl>
  )
}
